import {Event} from '@/apiTypes/event';
import {MiniMap} from '@/components/miniMap/miniMap';
import {useAnalytics} from '@/hooks/useAnalytics';

import {formatLocationShort} from '@/utils/formatLocationShort/formatLocationShort';
import {getNavigationUrl} from '@/utils/platform';
import {MapPin} from 'lucide-react';
import {useCallback, useRef} from 'react';
import {Typography} from '../ui/typography';
import {EventSection} from '../eventPage/eventSection';

/** Map card showing the event's starting location. */
export function LocationCard({event}: {event: Event}) {
  const hasLocation = event.lat !== 0 && event.lng !== 0;
  const {track} = useAnalytics();
  const hasTrackedRef = useRef(false);

  const handleMapInteraction = useCallback(() => {
    if (hasTrackedRef.current) return;
    hasTrackedRef.current = true;
    track('Location Map Interaction', {
      event_id: event.id,
      event_name: event.name,
    });
  }, [track, event.id, event.name]);

  if (!hasLocation) return null;

  const mapContent = (
    <MiniMap
      markers={[{lat: event.lat, lng: event.lng, id: event.id}]}
      options={{
        center: [event.lng, event.lat],
        zoom: 12,
      }}
    />
  );

  return (
    <EventSection label="Location">
      <div className="flex flex-col gap-2">
        <div className="flex items-start gap-2">
          <MapPin className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-gray-900">Starting Point</p>
            <p className="text-sm text-gray-600">
              {formatLocationShort(event.location)}
            </p>
          </div>
        </div>

        <a
          href={getNavigationUrl(event.lat, event.lng)}
          target="_blank"
          rel="noopener noreferrer"
          className="relative rounded-lg h-48 overflow-hidden block"
          onClick={handleMapInteraction}
        >
          <div className="pointer-events-none h-full">{mapContent}</div>
        </a>
      </div>
    </EventSection>
  );
}
